/**
 * WordPress dependencies.
 */
import { dispatch } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';
import { store as NOTICES } from '@safe-wordpress/notices';

/**
 * External dependencies.
 */
import {
	findLastIndex,
	keys,
	mapValues,
	omit,
	reduce,
	reverse,
	sortBy,
	values,
} from 'lodash';
import type { Dict, Maybe, SubscriptionPlan } from '@nab/types';

/* eslint-disable @wordpress/i18n-no-flanking-whitespace */
const AND = _x( ' and ', 'text (2 item list)', 'nelio-ab-testing' );
const AND_PLUS = _x( ', and ', 'text (2+ item list)', 'nelio-ab-testing' );
const OR = _x( ' or ', 'text (2 item list)', 'nelio-ab-testing' );
const OR_PLUS = _x( ', or ', 'text (2+ item list)', 'nelio-ab-testing' );
/* eslint-enable @wordpress/i18n-no-flanking-whitespace */

export function stringify( value: undefined ): undefined;
export function stringify( value: string | number ): string;
export function stringify(
	value: string | number | undefined
): string | undefined;
export function stringify(
	value: string | number | undefined
): string | undefined {
	return 'number' === typeof value ? value.toString() : value;
}

export const hasHead = < T >( arr: ReadonlyArray< T > ): arr is [ T, ...T[] ] =>
	arr.length > 0;

export const isSingleton = < T >( arr: ReadonlyArray< T > ): arr is [ T ] =>
	arr.length === 1;

export const isMultiArray = < T >(
	arr: ReadonlyArray< T >
): arr is [ T, T, ...T[] ] => arr.length > 1;

export const isNumber = ( n: unknown ): n is number => 'number' === typeof n;

export function logError( n: unknown ): void {
	// eslint-disable-next-line no-console
	console.log( '[NAB]', n );
}

export function listify(
	mode: 'and' | 'or',
	tokens: ReadonlyArray< string >
): string {
	if ( ! hasHead( tokens ) ) {
		return '';
	}

	if ( ! hasAtLeastTwoItems( tokens ) ) {
		return tokens[ 0 ];
	}

	const andor = (): string => {
		if ( 'and' === mode ) {
			return tokens.length === 2 ? AND : AND_PLUS;
		}
		return tokens.length === 2 ? OR : OR_PLUS;
	};

	const [ z, y, ...x ] = reverse( tokens );
	return [ ...reverse( x ), `${ y }${ andor() }${ z }` ].join( ', ' );
}

export const sum = ( a: Maybe< ReadonlyArray< number > > ): number =>
	a?.reduce( ( r, n ) => r + n, 0 ) ?? 0;

export async function createErrorNotice(
	error: unknown,
	defaultError?: string
): Promise< void > {
	if ( hasErrors( error ) ) {
		await dispatch( NOTICES ).createErrorNotice(
			values( error.errors )[ 0 ] ?? ''
		);
		return;
	}

	if ( hasMessage( error ) ) {
		await dispatch( NOTICES ).createErrorNotice( error.message );
		return;
	}

	if ( 'string' === typeof error ) {
		await dispatch( NOTICES ).createErrorNotice( error );
		return;
	}

	const message =
		defaultError ?? _x( 'Unknown error', 'text', 'nelio-ab-testing' );
	await dispatch( NOTICES ).createErrorNotice( message );
}

export function omitUndefineds< T extends Dict >( obj: T ): T {
	return omit(
		obj,
		keys( obj ).filter( ( k ) => undefined === obj[ k ] )
	) as T;
}

export function sortObjectKeysUsingValue< T extends Record< string, string > >(
	obj: T
): T {
	const collection = mapValues( obj, ( value, key ) => ( { key, value } ) );
	const unsortedList = values( collection );
	const sortedList = sortBy( unsortedList, 'value' );
	return reduce(
		sortedList,
		( acc, { key, value } ) => {
			acc[ key ] = value;
			return acc;
		},
		{} as Record< string, string >
	) as T;
}

export function getLetter( index: number ): string {
	const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXY';
	if ( index >= letters.length ) {
		return 'Z';
	}
	return letters.charAt( index );
}

export function isPlan(
	plan: SubscriptionPlan | false,
	mode: 'exactly' | 'exactly-or-above',
	expectedPlan: SubscriptionPlan
): boolean {
	if ( ! plan ) {
		return false;
	}

	const plans = [ 'basic', 'professional', 'enterprise' ];
	const expectedIndex = plans.indexOf( expectedPlan );
	const actualIndex = plans.indexOf( plan );

	if ( -1 === expectedIndex || -1 === actualIndex ) {
		return false;
	}

	if ( 'exactly-or-above' !== mode ) {
		return expectedIndex === actualIndex;
	}

	return actualIndex >= expectedIndex;
}

export function createIndexedSelector<
	TArgs extends ReadonlyArray< unknown >,
	TResult,
>(
	selector: ( ...args: TArgs ) => TResult,
	getDependants: ( ...args: TArgs ) => {
		readonly key: string | number;
		readonly dependants: ReadonlyArray< unknown >;
	}
): ( ...args: TArgs ) => TResult {
	const cache: Record<
		string,
		{
			readonly prevDependants: ReadonlyArray< unknown >;
			readonly result: TResult;
		}
	> = {};

	return ( ...args: TArgs ): TResult => {
		const { key, dependants } = getDependants( ...args );
		if (
			! cache[ key ] ||
			! areDependentsEqual( cache[ key ].prevDependants, dependants )
		) {
			cache[ key ] = {
				prevDependants: dependants,
				result: selector( ...args ),
			};
		}
		return cache[ key ].result;
	};
}

export function transformAlternativeChancesToAllocatedSlots(
	sizes: ReadonlyArray< number >,
	slots: number
): ReadonlyArray< number > {
	const min = 100 / slots;
	sizes = sizes.map( ( s ) => ( s >= min ? s : min ) );
	const result = sizes.map( ( s ) => Math.round( slots * ( s / 100 ) ) );

	while ( sum( result ) > slots ) {
		const maxIndex = findLastMax( result );
		// @ts-expect-error Index is in range.
		--result[ maxIndex ];
	}

	while ( sum( result ) < slots ) {
		for ( let i = 0; i < result.length; ++i ) {
			if ( sum( result ) >= slots ) {
				break;
			}
			// @ts-expect-error Index is in range.
			++result[ i ];
		}
	}

	return result;
}

// =======
// HELPERS
// =======

function areDependentsEqual(
	a: ReadonlyArray< unknown >,
	b: ReadonlyArray< unknown >
) {
	if ( a.length !== b.length ) {
		return false;
	}

	for ( let i = 0; i < a.length; ++i ) {
		if ( a[ i ] !== b[ i ] ) {
			return false;
		}
	}

	return true;
}

const hasAtLeastTwoItems = < T >(
	arr: ReadonlyArray< T >
): arr is [ T, T, ...T[] ] => arr.length >= 2;

const hasErrors = ( x: unknown ): x is { errors: Record< string, string > } =>
	!! values( ( x as Dict ).errors )[ 0 ];

const hasMessage = ( x: unknown ): x is { message: string } =>
	!! x &&
	'object' === typeof x &&
	'string' === typeof ( x as Dict ).message &&
	!! ( x as Dict ).message;

function findLastMax( a: ReadonlyArray< number > ): number {
	const max = a.reduce( ( r, v ) => ( v > r ? v : r ), 0 );
	return findLastIndex( a, ( v ) => v === max ) ?? 0;
}
